import type { AccountMember, AccountRole } from '@packages/types';
import { capturesOnBehalfOfAnother, isFeeEarnerRole } from '@packages/types';

/** The members who can be the performer of a time entry, and so can hold an hourly rate. */
export function feeEarnerMembers(members: AccountMember[]): AccountMember[] {
  return members.filter((member) => isFeeEarnerRole(member.role));
}

/**
 * The fee earner a viewer works for by default: themselves, unless they
 * capture on behalf of another (`capturesOnBehalfOfAnother`), in which case
 * the account's earliest owner (`createdAt` ascending, `id` as the tie-break).
 * A viewer who captures on behalf of another on an account with no owner
 * resolves to themselves.
 *
 * The backend applies the same rule to an entry saved without a performer
 * (`resolveDefaultPerformerUserId`, account-membership domain), sharing the
 * role predicate, so the form's pre-selected performer and the rate shown for
 * them match what the backend would attribute and price.
 */
export function defaultFeeEarnerUserId(
  members: AccountMember[],
  currentUserId: string,
  viewerRole: AccountRole | null,
): string {
  if (!capturesOnBehalfOfAnother(viewerRole)) return currentUserId;

  const owners = members.filter((member) => member.role === 'owner');
  if (owners.length === 0) return currentUserId;

  const earliestOwner = owners.reduce((earliest, candidate) => {
    if (candidate.createdAt < earliest.createdAt) return candidate;
    if (candidate.createdAt > earliest.createdAt) return earliest;
    return candidate.id < earliest.id ? candidate : earliest;
  });

  return earliestOwner.user.id;
}
